import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { useState, useEffect } from 'react';
import { supabase, type Project } from '../../lib/supabase';
import { useSEO, getWebPageSchema, getBreadcrumbSchema } from '../../utils/seo';

export default function PortfolioPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.21daybusinesswebsite.com';
  
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      getWebPageSchema(
        'Portfolio - Nos réalisations',
        'Découvrez nos réalisations de sites web business professionnels créés en 3 semaines pour nos clients.',
        `${siteUrl}/portfolio`
      ),
      getBreadcrumbSchema([
        { name: 'Accueil', url: '/' },
        { name: 'Portfolio', url: '/portfolio' }
      ]),
      {
        '@type': 'CollectionPage',
        name: 'Portfolio',
        description: 'Galerie de nos projets de sites web business'
      }
    ]
  };

  useSEO({
    title: 'Portfolio - Nos réalisations de sites web | THE 21-DAY BUSINESS WEBSITE™',
    description: 'Découvrez nos réalisations de sites web business professionnels créés en 3 semaines pour nos clients. Plus de 50 projets réussis.',
    keywords: 'portfolio web, réalisations sites web, exemples sites business, projets web, agence web portfolio',
    canonical: `${siteUrl}/portfolio`,
    schema
  });

  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error: fetchError } = await supabase
        .from('projects')
        .select('*')
        .order('display_order', { ascending: true });

      if (fetchError) throw fetchError;

      setProjects(data || []);
    } catch (err) {
      console.error('Erreur lors du chargement des projets:', err);
      setError('Impossible de charger les projets');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <main className="pt-20">
        {/* Hero */}
        <section className="py-24 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Notre <span className="text-yellow-500">Portfolio</span>
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
              Découvrez quelques-uns des sites business que nous avons créés pour nos clients
            </p>
          </div>
        </section>

        {/* Portfolio Grid */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            {loading && (
              <div className="text-center py-12">
                <div className="inline-block w-12 h-12 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
                <p className="mt-4 text-slate-600">Chargement des projets...</p>
              </div>
            )}

            {error && (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                  <i className="ri-error-warning-line text-3xl text-red-600"></i>
                </div>
                <p className="text-red-600 font-medium">{error}</p>
              </div>
            )}

            {!loading && !error && projects.length === 0 && (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-full mb-4">
                  <i className="ri-folder-open-line text-3xl text-slate-400"></i>
                </div>
                <p className="text-slate-600">Aucun projet à afficher</p>
              </div>
            )}

            {!loading && !error && projects.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {projects.map((project) => (
                  <div 
                    key={project.id}
                    className="group cursor-pointer"
                    onClick={() => setSelectedProject(project.id)}
                  >
                    <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300">
                      <div className="aspect-[4/3] overflow-hidden">
                        <img 
                          src={project.image_url}
                          alt={project.title}
                          className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                        />
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                        <div className="p-6 text-white">
                          <div className="text-sm text-yellow-500 mb-2">{project.category}</div>
                          <h3 className="text-xl font-bold mb-2">{project.title}</h3>
                          <p className="text-sm text-slate-300">{project.description}</p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4">
                      <div className="text-sm text-yellow-700 font-medium mb-1">{project.category}</div>
                      <h3 className="text-xl font-bold text-slate-900">{project.title}</h3>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Stats */}
        <section className="py-24 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-5xl font-bold text-yellow-700 mb-2">50+</div>
                <div className="text-slate-600">Sites créés</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-yellow-700 mb-2">100%</div>
                <div className="text-slate-600">Clients satisfaits</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-yellow-700 mb-2">21</div>
                <div className="text-slate-600">Jours en moyenne</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-yellow-700 mb-2">5★</div>
                <div className="text-slate-600">Note moyenne</div>
              </div>
            </div>
          </div>
        </section>

        {/* Modal */}
        {selectedProject && (
          <div 
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6"
            onClick={() => setSelectedProject(null)}
          >
            <div 
              className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative">
                <button
                  onClick={() => setSelectedProject(null)}
                  className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white rounded-full shadow-lg cursor-pointer hover:bg-slate-100 transition-colors z-10"
                >
                  <i className="ri-close-line text-2xl text-slate-900"></i>
                </button>
                <img 
                  src={projects.find(p => p.id === selectedProject)?.image_url}
                  alt={projects.find(p => p.id === selectedProject)?.title}
                  className="w-full h-96 object-cover object-top rounded-t-3xl"
                />
              </div>
              <div className="p-8">
                <div className="text-sm text-yellow-700 font-medium mb-2">
                  {projects.find(p => p.id === selectedProject)?.category}
                </div>
                <h3 className="text-3xl font-bold text-slate-900 mb-4">
                  {projects.find(p => p.id === selectedProject)?.title}
                </h3>
                <p className="text-lg text-slate-600 leading-relaxed">
                  {projects.find(p => p.id === selectedProject)?.description}
                </p>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
